import React, { useCallback, useMemo, useState, FunctionComponent } from 'react';
import { OpenCloudAPIObjectReferenceType, OpenCloudAPIObjectReference } from '../../utils/oasTypes';
import OpenCloudAPIObject from '../../utils/interfaces/OpenCloudAPIObject';
import isObjectReference from '../utils/isObjectReference';
import ObjectReferenceContext from '../ObjectReferenceContext';

export interface ObjectReferenceProviderProps {
  objects: OpenCloudAPIObject[];
}

const ObjectReferenceProvider: FunctionComponent<
  React.PropsWithChildren<ObjectReferenceProviderProps>
> = ({ objects, children }) => {
  const [responseSchemas, setResponseSchemas] = useState<Set<string>>(new Set());

  const objectReferences = useMemo(
    () => ({
      // Extend this for supporting more type of reference from "Components
      // Object" in OAS
      [OpenCloudAPIObjectReferenceType.Schema]: Object.fromEntries(
        objects.map((object) => [object.name, object]),
      ),
    }),
    [objects],
  );

  const dereference = useCallback(
    ({ refType, name }: OpenCloudAPIObjectReference): OpenCloudAPIObject | null =>
      objectReferences[refType]?.[name] ?? null,
    [objectReferences],
  );

  const addResponseSchema = useCallback(
    (name: string) => {
      setResponseSchemas((set) => {
        if (!set.has(name)) {
          return new Set(set).add(name);
        }
        return set;
      });
    },
    [setResponseSchemas],
  );

  const objectReferencesContextValue = useMemo(
    () => ({ isObjectReference, dereference, responseSchemas, addResponseSchema }),
    [addResponseSchema, dereference, responseSchemas],
  );

  return (
    <ObjectReferenceContext.Provider value={objectReferencesContextValue}>
      {children}
    </ObjectReferenceContext.Provider>
  );
};

export default ObjectReferenceProvider;
